import clsx from "clsx";
import { snakeCase } from "lodash";
import { FunctionComponent, ReactNode } from "react";
import { Icon, RenderWhen } from "../../../../../components";
import { Sizes } from "../../../../../components/Icon";
import Time from "../../../../../components/Time";
import Typography from "../../../../../components/typography/Typography";
import { LAYOUT_TYPE } from "../reanalyze-form-input/ReanalyzeFormInput";
import ReanalyzeFormInputLoadingIndicator from "../reanalyze-form-input/ReanalyzeFormInputLoadingIndicator";

type ReanalyzeFormInputLabelProps = {
  name: string;
  labelIcon: string;
  labelIconSize: Sizes;
  layout: string;
  disabled?: boolean;
  label: ReactNode;
  help?: string;
  helpImportant?: string | ReactNode | null;
  timestamp?: string;
  isLoadingAnalysisStatus?: boolean;
};

const ReanalyzeFormInputLabel: FunctionComponent<
  ReanalyzeFormInputLabelProps
> = props => (
  <div
    className={clsx(
      "d-flex align-items-center gap-5 cursor-pointer",
      props.disabled ? "cursor-not-allowed" : "cursor-pointer"
    )}
  >
    <Icon
      name={props.labelIcon}
      size={props.labelIconSize}
      dimensions={props.labelIconSize}
    />
    <div
      className={clsx(
        "d-flex disable-user-select",
        props.layout === LAYOUT_TYPE.ROW
          ? "align-items-center flex-row gap-10"
          : "align-items-baseline flex-column"
      )}
      style={{ maxWidth: 426 }}
    >
      <Typography
        variant={props.layout === LAYOUT_TYPE.ROW ? "h5" : "body1"}
        className={clsx(
          {
            "text-color-disabled":
              props.disabled && props.layout === LAYOUT_TYPE.COL
          },
          "text-nowrap"
        )}
      >
        {props.label}
      </Typography>
      {props.help && (
        <Typography
          variant={"subtitle6"}
          className={clsx(
            "fst-italic text-nowrap",
            props.disabled ? "text-color-disabled" : "text-color-red"
          )}
          data-testid={`reanalyze_form_help_text_${snakeCase(props.name)}`}
        >
          {props.help}
        </Typography>
      )}

      <RenderWhen
        condition={!props.isLoadingAnalysisStatus}
        fallback={<ReanalyzeFormInputLoadingIndicator />}
      >
        {props.helpImportant && (
          <>
            {props.layout === LAYOUT_TYPE.ROW && (
              <Typography variant={"h5"}>|</Typography>
            )}
            <span
              className={clsx(
                {
                  "text-color-disabled":
                    props.disabled && props.layout === LAYOUT_TYPE.COL
                },
                props.layout === LAYOUT_TYPE.COL && "fs-sm text-color-gray-60",
                "overflow-ellipsis"
              )}
              data-testid={`reanalyze_form_help_text_important_${snakeCase(
                props.name
              )}`}
            >
              {props.helpImportant}
            </span>
          </>
        )}
        {props.timestamp && (
          <>
            {props.layout === LAYOUT_TYPE.ROW && (
              <Typography variant={"h5"}>|</Typography>
            )}
            <span
              className="reanalyze-form-help-text"
              data-testid={`reanalyze_form_timestamp_${snakeCase(props.name)}`}
            >
              <Time
                className={clsx({
                  "text-color-disabled":
                    props.disabled && props.layout === LAYOUT_TYPE.COL
                })}
                time={props.timestamp}
              />
            </span>
          </>
        )}
      </RenderWhen>
    </div>
  </div>
);

export default ReanalyzeFormInputLabel;
